// Connected apps & AI agents (universal-LLM Phase 2): every OAuth connection
// the signed-in user has granted via consent or device approval — one row per
// refresh-token family. Revoking cuts the app off at its next token refresh.
// Self-scoped by the backend (listMyAuthorizations uses the caller's identity).
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Loader2, Plug, Bot, BadgeCheck, AlertTriangle } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  listMyAuthorizations,
  revokeMyAuthorization,
  type MyAuthorization,
} from '@/services/developerService';

export const ConnectedAppsCard: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const confirm = useConfirm();
  const [rows, setRows] = useState<MyAuthorization[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await listMyAuthorizations());
    } catch {
      setRows([]); // non-fatal: the card just shows the empty state
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (row: MyAuthorization) => {
    const ok = await confirm({
      title: t('settings:revoke_app_title', 'Disconnect {{name}}?', { name: row.appName }),
      message: t(
        'settings:revoke_app_desc',
        'It will lose access the next time it refreshes its connection. You can reconnect it later by authorizing it again.',
      ),
      confirmLabel: t('settings:revoke_app_cta', 'Disconnect'),
      danger: true,
    });
    if (!ok) return;
    setBusyId(row.familyId);
    try {
      await revokeMyAuthorization(row.familyId);
      toast.success(t('settings:revoke_app_done', '{{name}} disconnected', { name: row.appName }));
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('settings:revoke_app_failed', 'Could not disconnect the app')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plug className="w-5 h-5" /> {t('settings:connected_apps', 'Connected apps & AI agents')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('common:loading', 'Loading…')}
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t(
              'settings:connected_apps_empty',
              'Nothing is connected to your account. When you authorize an app or AI assistant (like Claude or a coding agent) to act on your behalf, it will appear here.',
            )}
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div
                key={row.familyId}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-border px-4 py-3"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {row.kind === 'agent' || row.kind === 'cli' ? (
                    <Bot className="w-4 h-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <Plug className="w-4 h-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="font-medium truncate">{row.appName}</span>
                  {row.certified && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                      <BadgeCheck className="w-3.5 h-3.5" /> {t('settings:app_certified', 'Certified')}
                    </span>
                  )}
                  {row.source === 'dcr' && !row.certified && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="w-3.5 h-3.5" /> {t('settings:app_unverified', 'Unverified')}
                    </span>
                  )}
                </div>
                <div className="basis-full sm:basis-auto sm:ml-auto flex items-center gap-3">
                  <span className="text-xs text-muted-foreground truncate max-w-[260px]">
                    {row.scopes.join(', ')}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busyId === row.familyId}
                    onClick={() => void revoke(row)}
                  >
                    {busyId === row.familyId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      t('settings:revoke_app_cta', 'Disconnect')
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConnectedAppsCard;
